import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useSubscriptions, useChangelog } from '../hooks/useSubscriptions';
import { useAuth } from '../hooks/useAuth';
import { useNotices } from '../hooks/useNotices';
import {
  useMigrationStatus,
  getMigrationStatusMessage,
  hasRemoteAccessStep,
  isRemoteAccessStepPending,
} from '../hooks/useMigrationStatus';
import AppLayout from '../components/layout/AppLayout';
import StatusBadge from '../components/common/StatusBadge';
import Spinner from '../components/common/Spinner';
import BackupList from '../components/backups/BackupList';
import UpgradePanel from '../components/services/UpgradePanel';
import AutoUpgradeToggle from '../components/services/AutoUpgradeToggle';
import CustomDomainTab, { UISP_CUSTOM_DOMAIN_NOTE } from '../components/services/CustomDomainTab';
import ResetPanel from '../components/services/ResetPanel';
import RebootPanel from '../components/services/RebootPanel';
import DeviceSearchPanel from '../components/services/DeviceSearchPanel';
import DeviceCount from '../components/services/DeviceCount';
import { getServiceLabel, isNodeUisp, primaryHostname } from '../utils/subscription';
import UosMigrationModal from '../components/services/UosMigrationModal';
import { UosCustomDomainMigration } from '../components/services/UosCustomDomainMigration';
import DedicatedIpDisplay from '../components/billing/DedicatedIpDisplay';
import InformUrlDisplay from '../components/services/InformUrlDisplay';
import { dismissMigration, requestUosMigration } from '../api/migration';
import { HELP_LINKS } from '../components/notices/helpLinks';

type Tab = 'overview' | 'controls' | 'backups' | 'changelog';

export default function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: subscriptions, isLoading, error } = useSubscriptions();
  const { user } = useAuth();
  // Same query key as AppLayout's useNotices(), so this doesn't trigger an extra fetch.
  const { notices } = useNotices();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const { data: changelog } = useChangelog(id || '');

  const subscription = subscriptions?.find((s) => s.id === id);
  const canOpenController = user?.role !== 'billing';

  // Poll migration status when migration is in progress, or for completed migrations (dismiss
  // state + the post-migration Remote Access step). The hook stops polling on its own once
  // the migration is active/failed and no step is left open (see getMigrationRefetchInterval).
  const shouldPollMigration = !!subscription?.migration_in_progress && !subscription?.migrated_to_uos;
  const hasMigration = shouldPollMigration || !!subscription?.migrated_to_uos;
  const { migrationStatus, isInProgress, isComplete } = useMigrationStatus({
    subscriptionId: id || '',
    enabled: hasMigration,
  });

  // Use polled status for real-time updates
  const migrationFailed = migrationStatus?.status === 'failed';
  const migrationInProgress = !migrationFailed && (isInProgress || (shouldPollMigration && !isComplete));
  const migrationComplete = isComplete || subscription?.migrated_to_uos;
  const uosHostname = migrationStatus?.uos_hostname || subscription?.uos_hostname;
  const canStartUosMigration =
    subscription?.app_name === 'UniFi' && !migrationComplete && !migrationInProgress && !!subscription.migration_eligible;

  // `?migrate=uos` (uosUpgradeHref, the standalone-eol notice's Upgrade button) lands on Controls
  // with the upgrade window open. The notice shows on every page, this one included, so the param
  // is what makes a click here do anything. Cleared once handled, so the next click (after a
  // Cancel, say) changes the URL again.
  const [searchParams, setSearchParams] = useSearchParams();
  const uosUpgradeRequested = searchParams.get('migrate') === 'uos';
  useEffect(() => {
    // Subscriptions still loading: keep the param so this can fire once they land.
    if (!uosUpgradeRequested || !subscription) return;
    // Microtask defer keeps the state updates off the effect's synchronous path
    // (react-hooks/set-state-in-effect), same idiom as useUpgradeDeepLink.
    void Promise.resolve().then(() => {
      setActiveTab('controls');
      if (canStartUosMigration) setShowMigrationModal(true);
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('migrate');
        return next;
      },
      { replace: true }
    );
  }, [uosUpgradeRequested, subscription, canStartUosMigration, setSearchParams]);

  // `?tab=controls` (the post-migration emails' portal link) lands on Controls, where the
  // "Upgraded to UniFi OS" panel lives. Same idiom as ?migrate=uos; only 'controls' is accepted,
  // the other tabs have no caller. Cleared once handled.
  const controlsTabRequested = searchParams.get('tab') === 'controls';
  useEffect(() => {
    if (!controlsTabRequested) return;
    void Promise.resolve().then(() => setActiveTab('controls'));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true }
    );
  }, [controlsTabRequested, setSearchParams]);

  // Post-migration Remote Access step. Shown only when the source NWA had Remote Access on
  // AND the owner is cloud-linked (a local-only owner chose local on purpose; no nag).
  // 'done' once the UOS console reports it back on, otherwise the customer must acknowledge
  // it before Done (they may legitimately no longer want it).
  const remoteAccessStep: 'none' | 'done' | 'pending' | 'unknown' =
    !hasRemoteAccessStep(migrationStatus) ? 'none'
    : migrationStatus!.uos_remote_access_enabled === true ? 'done'
    : migrationStatus!.uos_remote_access_enabled === false ? 'pending'
    : 'unknown';
  const remoteAccessStepOpen = isRemoteAccessStepPending(migrationStatus);

  // Post-migration custom domain step, driven by the LIVE custom_hostnames so a domain
  // swapped after migration is what the customer is asked to re-point. Same rule as the
  // standalone card it replaces; Done stays gated on custom_domain_migration_status.
  const liveCustomHostnames = subscription?.custom_hostnames ?? [];
  const customDomainStepShown =
    !!migrationStatus?.has_migration &&
    migrationStatus.custom_domain_migration_status != null &&
    !!subscription?.uos_hostname &&
    liveCustomHostnames.length > 0;
  const [remoteAccessAcknowledged, setRemoteAccessAcknowledged] = useState(false);
  const remoteAccessGateSatisfied = !remoteAccessStepOpen || remoteAccessAcknowledged;

  // Dismiss post-migration UI
  const dismissMutation = useMutation({
    mutationFn: () => dismissMigration(subscription!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['migration-status', subscription!.id] });
    },
  });

  // Request UOS migration access (VPS customers)
  const requestMigrationMutation = useMutation({
    mutationFn: () => requestUosMigration(subscription!.id),
  });

  // Refresh subscription data when migration completes (guard against duplicate invalidation on remount)
  const hasInvalidatedRef = useRef(false);
  useEffect(() => {
    if (isComplete && !hasInvalidatedRef.current) {
      hasInvalidatedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    }
  }, [isComplete, queryClient]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error || !subscription) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Service Not Found</h2>
          <p className="text-gray-500 mb-4">The service you're looking for doesn't exist.</p>
          <Link
            to="/services"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to Services
          </Link>
        </div>
      </AppLayout>
    );
  }

  // Determine if this is a UOS subscription (migrated OR a fresh UOS signup)
  const isMigrated = subscription.migrated_to_uos && subscription.uos_hostname;
  // Fresh UOS signups (2026-09) have no uos_migrations row, so the API sends
  // migration_completed_at=null. Only a real migration gets the "Migrated" badge and the
  // "Originally"/"Original Hostname" lines — for a fresh signup the .cloudunifi.com hostname is
  // just our internal id and means nothing to the customer.
  const hasMigrationRecord = !!isMigrated && !!subscription.migration_completed_at;

  // The customer-facing name (design §20): UOS console name, a node UISP's netconsole.cloud
  // name, else the hostname. One value for the header, Hostname row, NetFlow line and CNAME target.
  const customerName = primaryHostname(subscription);
  // Prefer the customer's custom domain, else the customer-facing name
  const displayHostname = subscription.custom_hostnames?.[0] || customerName;

  // UISP on one of our nodes (netflow_port from the API). A legacy VPS UISP keeps today's page.
  const nodeUisp = isNodeUisp(subscription);

  // Controller URL: UOS routes don't use a port; legacy UniFi uses :8443
  const controllerUrl = isMigrated
    ? `https://${displayHostname}`
    : `https://${displayHostname}${subscription.app_name === 'UniFi' ? ':8443' : ''}`;

  // Inform URL uses the same host precedence devices are provisioned with
  // (custom domain, else UOS hostname, else legacy hostname)
  const informUrl = `http://${displayHostname}:8080/inform`;

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/services"
          className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Services
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{displayHostname}</h1>
            <div className="flex items-center gap-3 mt-1">
              {isMigrated ? (
                <>
                  <span className="text-gray-500">UniFi OS</span>
                  {hasMigrationRecord && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Migrated
                    </span>
                  )}
                </>
              ) : (
                <span className="text-gray-500">{subscription.app_name}</span>
              )}
              <StatusBadge status={subscription.status} />
            </div>
            {/* Show original hostname for migrated subscriptions (not fresh UOS signups) */}
            {hasMigrationRecord && (
              <p className="text-sm text-gray-400 mt-1">
                Originally: {subscription.hostname}
              </p>
            )}
          </div>
          {canOpenController && (
            <a
              href={controllerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              {subscription.app_name === 'UISP' ? 'Open UISP' : 'Open Controller'}
            </a>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('controls')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'controls'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Controls
          </button>
          <button
            onClick={() => setActiveTab('backups')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'backups'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Backup
          </button>
          <button
            onClick={() => setActiveTab('changelog')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'changelog'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Changelog
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h2>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Primary hostname - show UOS for migrated */}
            <div>
              <dt className="text-sm font-medium text-gray-500">
                {isMigrated ? 'UOS Hostname' : 'Hostname'}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {customerName}
              </dd>
            </div>

            {/* Show original hostname for migrated subscriptions (not fresh UOS signups) */}
            {hasMigrationRecord && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Original Hostname</dt>
                <dd className="mt-1 text-sm text-gray-900">{subscription.hostname}</dd>
              </div>
            )}

            {/* Custom domain - only show for non-migrated */}
            {!isMigrated && subscription.custom_hostnames?.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {subscription.custom_hostnames.length > 1 ? 'Custom Domains' : 'Custom Domain'}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {subscription.custom_hostnames.join(', ')}
                </dd>
              </div>
            )}

            {/* NetFlow collector - node UISP only. Routers export to the service's name
                (customerName; never an IP: the node address can change) on this UDP port.
                Hidden while suspended/archived/cancelled: the hostname is proxied then,
                so UDP sent to it goes nowhere (same gate as the Inform URL). */}
            {nodeUisp &&
              (subscription.status === 'active' || subscription.status === 'setup_mode') && (
              <div>
                <dt className="text-sm font-medium text-gray-500">NetFlow collector</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="font-mono break-all">{`${customerName}:${subscription.netflow_port} (UDP)`}</span>
                  <a
                    href="https://help.cloudunifi.com/uisp/connecting-devices/#netflow"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-blue-600 hover:text-blue-800"
                  >
                    How to send NetFlow
                  </a>
                </dd>
              </div>
            )}

            {/* Inform URL - UniFi only (UISP has no inform protocol); hidden while
                suspended/archived/cancelled since the hostname is Bulk-Redirected or
                the server is decommissioned and set-inform against it cannot succeed */}
            {subscription.app_name === 'UniFi' &&
              (subscription.status === 'active' || subscription.status === 'setup_mode') && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Inform URL</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <InformUrlDisplay url={informUrl} />
                </dd>
              </div>
            )}

            {subscription.plan && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Plan</dt>
                <dd className="mt-1 text-sm text-gray-900">{subscription.plan}</dd>
              </div>
            )}

            {(subscription.dedicated_ip || subscription.dedicated_ip_status) && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Dedicated IP</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <DedicatedIpDisplay
                    status={subscription.dedicated_ip_status ?? null}
                    ip={subscription.dedicated_ip ?? null}
                  />
                </dd>
              </div>
            )}

            {subscription.device_count !== null && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Devices</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {subscription.max_devices !== null ? (
                    <DeviceCount
                      subscriptionId={subscription.id}
                      plan={subscription.plan}
                      current={subscription.device_count}
                      max={subscription.max_devices}
                    />
                  ) : (
                    subscription.device_count
                  )}
                </dd>
              </div>
            )}

            {/* Version - show UOS version for migrated, legacy version otherwise */}
            {(subscription.software_version || subscription.uos_software_version) && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {isMigrated ? 'Network Application Version' : 'Version'}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {isMigrated
                    ? subscription.uos_software_version || subscription.software_version || 'Unknown'
                    : subscription.software_version}
                </dd>
              </div>
            )}

            {isMigrated && subscription.uos_version && (
              <div>
                <dt className="text-sm font-medium text-gray-500">UOS Version</dt>
                <dd className="mt-1 text-sm text-gray-900">{subscription.uos_version}</dd>
              </div>
            )}

            {subscription.start_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(subscription.start_date).toLocaleDateString()}
                </dd>
              </div>
            )}

            {/* Migration date for migrated subscriptions */}
            {isMigrated && subscription.migration_completed_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Migrated On</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(subscription.migration_completed_at).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {activeTab === 'controls' && (
        <div className="space-y-6">
          {/* UniFi-specific sections */}
          {subscription.app_name === 'UniFi' && (
            <>
              {/* Migration in progress indicator - auto-refreshes via polling */}
              {migrationInProgress && uosHostname && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-amber-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Migration in Progress</p>
                      <p className="text-sm text-amber-700">
                        {getMigrationStatusMessage(migrationStatus) || (
                          <>
                            Your new UniFi OS instance is being provisioned at{' '}
                            <span className="font-medium">{uosHostname}</span>
                          </>
                        )}
                      </p>
                      <p className="text-sm text-amber-600 mt-1">
                        This process typically takes 20-30 minutes to complete. This page will update automatically.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Migration failed indicator */}
              {migrationFailed && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">Migration Failed</p>
                      <p className="text-sm text-red-700">
                        {getMigrationStatusMessage(migrationStatus)}
                      </p>
                      <p className="text-sm text-red-600 mt-1">
                        Please contact <a href="mailto:support@cloudunifi.com" className="underline hover:text-red-800">support@cloudunifi.com</a> for assistance.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Already migrated indicator */}
              {migrationComplete && uosHostname && migrationStatus?.has_migration && !migrationStatus.migration_dismissed_at && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800">Upgraded to UniFi OS</p>
                      <p className="text-sm text-green-700">
                        Your UOS instance:{' '}
                        <a
                          href={`https://${uosHostname}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-green-900"
                        >
                          {uosHostname}
                        </a>
                      </p>
                      {(migrationStatus?.owner_username || remoteAccessStep !== 'none' || customDomainStepShown) && (
                        <div className="mt-3 text-sm text-green-700 space-y-2">
                          <p className="font-medium text-green-800">Next steps:</p>
                          <ol className="list-decimal list-inside space-y-1 ml-1">
                            {/* The login and cloud-login steps need the owner (null on pre-0060
                                rows); the others do not. Cloud login sits after Remote Access
                                because email + SSO login waits on it. */}
                            {migrationStatus?.owner_username && (
                            <li>
                              <span className="font-medium text-green-800">Log in</span>
                              <div>
                                Log in to your UOS console as the Owner account you selected,{' '}
                                <span className="font-mono font-semibold bg-green-100 px-1.5 py-0.5 rounded">
                                  {migrationStatus.owner_username}
                                </span>
                                , with the password you set for it on your old controller. Type the
                                username, not an email address. We never store passwords.
                              </div>
                            </li>
                            )}
                            {remoteAccessStep !== 'none' && (
                              <li>
                                <span className="font-medium text-green-800">Remote Access</span>
                                {remoteAccessStep === 'done' ? (
                                  <div className="flex items-start gap-1.5">
                                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Enabled on your new UniFi OS console. Nothing more to do here.</span>
                                  </div>
                                ) : (
                                  <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
                                    <p className="font-medium">
                                      {remoteAccessStep === 'pending'
                                        ? 'Remote Access was on for your old controller and is still turned off on your new UniFi OS console.'
                                        : 'Remote Access was on for your old controller. We could not confirm whether Remote Access is enabled on your new UniFi OS console yet.'}
                                    </p>
                                    {/* UniFi OS registers Remote Access only with the Ubiquiti account
                                        linked to the Owner; any other account fails (475f90, 2026-11-03). */}
                                    <p className="mt-1">
                                      {migrationStatus?.owner_username ? (
                                        <>
                                          To turn it back on, log in to the UniFi OS console as the Owner,{' '}
                                          <span className="font-mono font-semibold">{migrationStatus.owner_username}</span>, go to{' '}
                                          <span className="font-medium">OS Settings &gt; Console</span> and toggle{' '}
                                          <span className="font-medium">Remote Access</span>. When it asks you to sign in,
                                          use the Ubiquiti account linked to {migrationStatus.owner_username}
                                          {migrationStatus.owner_email && (
                                            <>, usually <span className="font-mono">{migrationStatus.owner_email}</span></>
                                          )}
                                          . Only that Ubiquiti account can turn it on. Any other account fails with
                                          “We encountered an unexpected issue”.
                                        </>
                                      ) : (
                                        <>
                                          To turn it back on, open the UniFi OS console, go to{' '}
                                          <span className="font-medium">OS Settings &gt; Console</span> and toggle{' '}
                                          <span className="font-medium">Remote Access</span>. When it asks you to sign in,
                                          use your Ubiquiti account (from account.ui.com), not your local admin login.
                                        </>
                                      )}
                                    </p>
                                    <p className="mt-1">
                                      <a
                                        href="https://help.cloudunifi.com/unifi/using-ubiquiti-account/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline hover:text-amber-950"
                                      >
                                        See the Remote Access guide
                                      </a>
                                    </p>
                                  </div>
                                )}
                              </li>
                            )}
                            {migrationStatus?.owner_username && (
                            <li>
                              <span className="font-medium text-green-800">Ubiquiti cloud (email + SSO) login</span>
                              {migrationStatus.owner_cloud_linked === true ? (
                                <div>
                                  {remoteAccessStep === 'done' ? 'You ' : (<>
                                    Available once{' '}
                                    {remoteAccessStep === 'none'
                                      ? (<>you turn on Remote Access in <span className="font-medium">OS Settings &gt; Console</span></>)
                                      : 'Remote Access is on (the step above)'}
                                    . After that you{' '}
                                  </>)}
                                  can also log in at{' '}
                                  <a
                                    href={`https://${uosHostname}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-green-900"
                                  >
                                    {uosHostname}
                                  </a>{' '}
                                  using{' '}
                                  {migrationStatus.owner_email
                                    ? <span className="font-mono">{migrationStatus.owner_email}</span>
                                    : 'your Ubiquiti email'}
                                  {' '}and your Ubiquiti account password + MFA.
                                  {remoteAccessStep !== 'done' && ' Until then, use your username and password.'}
                                </div>
                              ) : (
                                <div>
                                  Not enabled. Local username + password is the only login method
                                  right now. To enable Ubiquiti cloud login: open{' '}
                                  <a href="https://account.ui.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">
                                    account.ui.com
                                  </a>
                                  , sign in with the email you want to use, then in the UOS controller
                                  go to <span className="font-medium">Identity → Owner → Link Ubiquiti
                                  Account</span>. After linking, email login will work.
                                </div>
                              )}
                            </li>
                            )}
                            {customDomainStepShown && (
                              <li>
                                <span className="font-medium text-green-800">
                                  {liveCustomHostnames.length > 1 ? 'Custom domains' : 'Custom domain'}
                                </span>
                                <UosCustomDomainMigration
                                  subscriptionId={subscription.id}
                                  customHostnames={liveCustomHostnames}
                                  uosHostname={subscription.uos_hostname!}
                                  status={migrationStatus.custom_domain_migration_status ?? null}
                                  error={migrationStatus.custom_domain_error}
                                />
                              </li>
                            )}
                          </ol>
                        </div>
                      )}
                      {/* Done button — only show when all migration tasks are resolved.
                          Judged on the LIVE custom_hostnames (mirrors the backend dismiss rule),
                          not the migration-time snapshot. */}
                      {((subscription.custom_hostnames ?? []).length === 0 ||
                        migrationStatus?.custom_domain_migration_status === 'active') && (
                        <div className="mt-4">
                          {remoteAccessStepOpen && (
                            <label className="flex items-start gap-2 text-sm text-green-800 mb-3 cursor-pointer">
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                checked={remoteAccessAcknowledged}
                                onChange={(e) => setRemoteAccessAcknowledged(e.target.checked)}
                              />
                              <span>I have re-enabled Remote Access, or I no longer need it.</span>
                            </label>
                          )}
                          <button
                            onClick={() => dismissMutation.mutate()}
                            disabled={dismissMutation.isPending || !remoteAccessGateSatisfied}
                            className="px-5 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {dismissMutation.isPending ? 'Dismissing...' : 'Done'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* UOS Service Info - show only for migrated */}
              {isMigrated && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">UniFi OS Service</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Your service has been migrated to UniFi OS. Software updates, backups, and
                    other management are handled directly in the UOS console.
                  </p>
                  <a
                    href={controllerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Open UOS Console
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
            </>
          )}

          {/* Legacy controls - UniFi non-migrated only */}
          {!isMigrated && subscription.app_name === 'UniFi' && (
            <>
              {/* Upgrade Controls */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Software Upgrades</h2>
                <AutoUpgradeToggle
                  subscriptionId={subscription.id}
                  autoUpgrade={subscription.auto_upgrade}
                  appName={subscription.app_name}
                />
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <UpgradePanel
                    subscriptionId={subscription.id}
                    currentVersion={subscription.software_version || 'Unknown'}
                  />
                </div>
              </div>

              {/* UOS Migration Banner - show only if not migrated, not in progress, and eligible */}
              {canStartUosMigration && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Upgrade to UniFi OS Available
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        UniFi OS is the platform Ubiquiti is building around the Network Application
                        you already use. Your Network Application and all its settings carry over
                        — the main difference you'll notice is an updated interface and a new
                        dashboard that supports additional services beyond networking.
                      </p>
                      <p className="text-xs text-gray-500 mb-4">
                        If you click "Remind Me Later" the banner should not pop up again until a
                        future software release upgrade resets it.
                      </p>
                      {/* Gated on the usg-eol notice being live (not just dismissed) so this
                          note can't outrun the usg-eol comms rollout: it rides the same
                          portal_notice_launches row, so it never shows before the copy is
                          reviewed and the help article is published. Closing the notice must
                          not hide this note, so dismissed status is ignored here. */}
                      {subscription.has_legacy_gateway && notices.some((n) => n.key === 'usg-eol') && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                          This service has a USG gateway adopted. It keeps working after the move. Once Network 11 is
                          released for UniFi OS Server, it keeps running with its current settings but can't be changed
                          or adopted again, so plan to replace it with a UXG gateway before then.{' '}
                          <a href={HELP_LINKS.usg} target="_blank" rel="noopener noreferrer" className="font-medium underline">
                            Read the USG guide
                          </a>
                        </p>
                      )}
                      <button
                        onClick={() => setShowMigrationModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
                      >
                        Upgrade to UniFi OS
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* UOS Migration Request - show for VPS subscriptions not yet eligible */}
              {!migrationComplete && !migrationInProgress && !subscription.migration_eligible && subscription.migration_requestable && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        UniFi OS Migration
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        UniFi OS is the platform Ubiquiti is building around the Network Application
                        you already use. Your Network Application and all its settings carry over
                        — the main difference you'll notice is an updated interface and a new
                        dashboard that supports additional services beyond networking.
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        There's no rush to migrate, and your current setup will continue working
                        as-is. That said, we are rolling out a migration option and can enable it
                        for your account.
                      </p>
                      {requestMigrationMutation.isSuccess || requestMigrationMutation.isError ? (
                        <p className="text-sm text-green-700 font-medium">
                          Request submitted — we'll be in touch to enable migration for your account.
                        </p>
                      ) : (
                        <button
                          onClick={() => requestMigrationMutation.mutate()}
                          disabled={requestMigrationMutation.isPending}
                          className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {requestMigrationMutation.isPending ? 'Submitting...' : 'Request Migration Access'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Custom Domain — all UniFi subscriptions (NWA and UOS) and UISP on a node.
              A node UISP: no inform host, no UOS DNS check, plus the UISP hostname note. */}
          {(subscription.app_name === 'UniFi' || nodeUisp) && (
            <CustomDomainTab
              subscriptionId={subscription.id}
              hostname={customerName}
              customHostnames={subscription.custom_hostnames ?? []}
              maxCustomHostnames={subscription.max_custom_hostnames ?? 1}
              informOverrideHost={nodeUisp ? null : (subscription.inform_override_host ?? null)}
              checkDns={!!isMigrated}
              platformNote={nodeUisp ? UISP_CUSTOM_DOMAIN_NOTE : undefined}
            />
          )}

          {/* Device Search — all UniFi subscriptions */}
          {subscription.app_name === 'UniFi' && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-md font-medium text-gray-900 mb-2">Device Search</h3>
              <DeviceSearchPanel subscriptionId={subscription.id} />
            </div>
          )}

          {/* Restart Service - available for ALL subscription types */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-md font-medium text-gray-900 mb-2">
              Restart {getServiceLabel(subscription.app_name, !!subscription.migrated_to_uos)}
            </h3>
            <RebootPanel
              subscriptionId={subscription.id}
              appName={subscription.app_name}
              isMigrated={!!subscription.migrated_to_uos}
            />
          </div>

          {/* Danger Zone - UniFi non-migrated only */}
          {!isMigrated && subscription.app_name === 'UniFi' && (
            <div className="bg-white shadow rounded-lg border border-red-200">
              <div className="px-6 py-4 border-b border-red-200 bg-red-50">
                <h2 className="text-lg font-semibold text-red-700">Danger Zone</h2>
              </div>
              <div className="p-6">
                <h3 className="text-md font-medium text-gray-900 mb-2">Reset Controller</h3>
                <ResetPanel subscriptionId={subscription.id} />
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'backups' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Backups</h2>
          <p className="text-sm text-gray-500 mb-6">
            Backups from the last 7 days. Click download to save a backup file.
          </p>
          <BackupList subscriptionId={subscription.id} />
        </div>
      )}

      {activeTab === 'changelog' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Changelog</h2>
          {changelog && changelog.length > 0 ? (
            <div className="space-y-3">
              {changelog.map((entry) => (
                <div key={entry.id} className="flex justify-between border-b border-gray-100 pb-2 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{entry.user_log}</p>
                    {entry.display_text && (
                      <p className="text-sm text-gray-500">{entry.display_text}</p>
                    )}
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No activity recorded yet.</p>
          )}
        </div>
      )}

      <UosMigrationModal
        isOpen={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        subscriptionId={subscription.id}
        customHostname={subscription.custom_hostnames?.[0] ?? null}
      />
    </AppLayout>
  );
}
